import { useEffect, useRef, useState } from 'react';
import { gameSounds } from '../../lib/gameSounds';

interface FeudAnswer {
  answer: string;
  points: number;
  synonyms?: string[];
}
interface FeudSurvey {
  question: string;
  answers: FeudAnswer[];
}
interface FeudStrikeEvent {
  viewer: string;
  strikeCount: number;
  lockedOut: boolean;
  ts: number;
}
interface FeudState {
  currentSurvey: FeudSurvey | null;
  revealed: number[];
  scores: Record<string, number>;
  strikes?: Record<string, number>;
  lockedOut?: string[];
  lastStrike?: FeudStrikeEvent | null;
  endsAt: number;
  finished: boolean;
}

/**
 * Family Feud overlay — gameshow board with flip cards, "SURVEY SAYS!"
 * intro flourish, animated red-X strike stamp, and a recent-strikes ticker.
 *
 * Sound triggers are wired against the shared gameSounds manager; they no-op
 * silently until pack assets register, matching the other widgets.
 */
export function FeudBody({ state }: { state: FeudState; config: unknown }) {
  const top = topScores(state.scores, 5);
  const surveyId = state.currentSurvey?.question ?? '';
  const prevSurveyIdRef = useRef<string>('');
  const prevRevealedCountRef = useRef<number>(0);
  const prevStrikeTsRef = useRef<number>(0);
  const prevFinishedRef = useRef<boolean>(false);
  const [showSurveySays, setShowSurveySays] = useState(false);
  const [strikeStamp, setStrikeStamp] = useState<FeudStrikeEvent | null>(null);

  // Configure sound manager once on mount. Volume defaults handled by the
  // widget chrome; we just use whatever's already configured.
  useEffect(() => {
    // gameSounds.configure is owner-managed at the widget chrome level for
    // dedicated games (hype-deal etc.); for the unified standard widget the
    // chrome configures it. We just fire effects.
  }, []);

  // First reveal of a new survey → "SURVEY SAYS!" intro flourish.
  useEffect(() => {
    if (!surveyId || surveyId === prevSurveyIdRef.current) return;
    prevSurveyIdRef.current = surveyId;
    prevRevealedCountRef.current = 0;
    setShowSurveySays(true);
    void gameSounds.play('game_start');
    void gameSounds.play('reveal');
    const t = setTimeout(() => setShowSurveySays(false), 2200);
    return () => clearTimeout(t);
  }, [surveyId]);

  // New reveal → ding.
  useEffect(() => {
    const count = state.revealed.length;
    if (count > prevRevealedCountRef.current) {
      void gameSounds.play('correct');
      prevRevealedCountRef.current = count;
    } else if (count < prevRevealedCountRef.current) {
      // Round reset — keep counter in sync.
      prevRevealedCountRef.current = count;
    }
  }, [state.revealed.length]);

  // New strike → red X stamp animation.
  useEffect(() => {
    const ls = state.lastStrike;
    if (!ls) return;
    if (ls.ts <= prevStrikeTsRef.current) return;
    prevStrikeTsRef.current = ls.ts;
    setStrikeStamp(ls);
    void gameSounds.play('incorrect');
    if (ls.lockedOut) void gameSounds.play('loser');
    const t = setTimeout(() => setStrikeStamp(null), 1600);
    return () => clearTimeout(t);
  }, [state.lastStrike]);

  // End-of-round fanfare.
  useEffect(() => {
    if (state.finished && !prevFinishedRef.current) {
      void gameSounds.play('winner');
      void gameSounds.play('celebration');
    }
    prevFinishedRef.current = state.finished;
  }, [state.finished]);

  if (state.finished) {
    return <Finished top={top} />;
  }
  if (!state.currentSurvey) {
    return (
      <div className="fd-stage">
        <div className="fd-loading">
          <div className="fd-loading-eyebrow">Preparing the next survey…</div>
          <div className="fd-loading-spinner" />
        </div>
        <FeudStyles />
      </div>
    );
  }

  const ranked = state.currentSurvey.answers
    .map((a, i) => ({ ...a, originalIndex: i }))
    .sort((a, b) => b.points - a.points);

  const leftCol = ranked.filter((_, i) => i % 2 === 0);
  const rightCol = ranked.filter((_, i) => i % 2 === 1);

  const recentStrikes = mostRecentStrikers(state.strikes ?? {}, state.lockedOut ?? [], 4);

  return (
    <div className="fd-stage">
      <div className="fd-spotlight" />
      <div className="fd-spotlight fd-spotlight-2" />

      <div className="fd-banner">
        <div className="fd-banner-eyebrow">Survey says…</div>
        <div className="fd-banner-prompt">{state.currentSurvey.question}</div>
      </div>

      <div className="fd-board">
        <div className="fd-col">
          {leftCol.map((a, i) => (
            <AnswerCard
              key={a.originalIndex}
              rank={i * 2 + 1}
              answer={a.answer}
              points={a.points}
              revealed={state.revealed.includes(a.originalIndex)}
            />
          ))}
        </div>
        <div className="fd-col">
          {rightCol.map((a, i) => (
            <AnswerCard
              key={a.originalIndex}
              rank={i * 2 + 2}
              answer={a.answer}
              points={a.points}
              revealed={state.revealed.includes(a.originalIndex)}
            />
          ))}
        </div>
      </div>

      <div className="fd-footer">
        <Timer endsAt={state.endsAt} />
        {recentStrikes.length > 0 && <StrikesTicker entries={recentStrikes} />}
      </div>

      {showSurveySays && <SurveySaysFlourish />}
      {strikeStamp && <StrikeStamp event={strikeStamp} />}

      <FeudStyles />
    </div>
  );
}

function AnswerCard({
  rank,
  answer,
  points,
  revealed,
}: {
  rank: number;
  answer: string;
  points: number;
  revealed: boolean;
}) {
  return (
    <div className={`fd-card ${revealed ? 'revealed' : ''}`}>
      <div className="fd-card-inner">
        <div className="fd-card-face fd-card-front">
          <span className="fd-card-rank">{rank}</span>
          <span className="fd-card-q">?</span>
        </div>
        <div className="fd-card-face fd-card-back">
          <span className="fd-card-rank fd-card-rank-revealed">{rank}</span>
          <span className="fd-card-answer">{answer.toUpperCase()}</span>
          <span className="fd-card-points">{points}</span>
        </div>
      </div>
    </div>
  );
}

function SurveySaysFlourish() {
  return (
    <div className="fd-survey-says">
      <div className="fd-survey-says-text">SURVEY SAYS!</div>
    </div>
  );
}

function StrikeStamp({ event }: { event: FeudStrikeEvent }) {
  return (
    <div className="fd-strike-stamp" key={event.ts}>
      <div className="fd-strike-x">✕</div>
      <div className="fd-strike-label">
        <div className="fd-strike-name">{event.viewer}</div>
        <div className="fd-strike-count">
          {event.lockedOut ? `OUT — ${event.strikeCount} strikes` : `Strike ${event.strikeCount}`}
        </div>
      </div>
    </div>
  );
}

function StrikesTicker({
  entries,
}: {
  entries: Array<{ name: string; strikes: number; lockedOut: boolean }>;
}) {
  return (
    <div className="fd-strikes-ticker">
      <span className="fd-strikes-label">STRIKES</span>
      {entries.map((e) => (
        <span
          key={e.name}
          className={`fd-strike-chip ${e.lockedOut ? 'locked' : ''}`}
          title={e.lockedOut ? 'Locked out for the round' : `${e.strikes} strikes`}
        >
          <span className="fd-strike-chip-name">{e.name}</span>
          <span className="fd-strike-chip-x">
            {Array.from({ length: e.strikes }, (_, i) => (
              <span key={i}>✕</span>
            ))}
          </span>
        </span>
      ))}
    </div>
  );
}

function Timer({ endsAt }: { endsAt: number }) {
  const [remaining, setRemaining] = useState(() => Math.max(0, (endsAt - Date.now()) / 1000));
  useEffect(() => {
    const tick = () => setRemaining(Math.max(0, (endsAt - Date.now()) / 1000));
    tick();
    const t = setInterval(tick, 250);
    return () => clearInterval(t);
  }, [endsAt]);
  const m = Math.floor(remaining / 60);
  const s = Math.floor(remaining % 60)
    .toString()
    .padStart(2, '0');
  const warn = remaining < 10;
  return (
    <div className={`fd-timer ${warn ? 'warn' : ''}`}>
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      >
        <circle cx="12" cy="13" r="8" />
        <path d="M12 9v4l2 2M9 2h6" />
      </svg>
      <span className="fd-timer-num">
        {m}:{s}
      </span>
    </div>
  );
}

function Finished({ top }: { top: Array<[string, number]> }) {
  return (
    <div className="fd-stage fd-stage-end">
      <div className="fd-end-card">
        <div className="fd-end-eyebrow">Survey complete</div>
        <div className="fd-end-title">{top[0] ? `${top[0][0]} wins` : 'No one scored'}</div>
        {top.length > 0 && (
          <div className="fd-end-board">
            {top.map(([name, score], i) => (
              <div key={name} className={`fd-end-row ${i === 0 ? 'top' : ''}`}>
                <span className="fd-end-rank">#{i + 1}</span>
                <span className="fd-end-name">{name}</span>
                <span className="fd-end-score">{score}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <FeudStyles />
    </div>
  );
}

function topScores(scores: Record<string, number>, n: number): Array<[string, number]> {
  return Object.entries(scores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n);
}

function mostRecentStrikers(
  strikes: Record<string, number>,
  lockedOut: string[],
  n: number,
): Array<{ name: string; strikes: number; lockedOut: boolean }> {
  const lockedSet = new Set(lockedOut);
  return Object.entries(strikes)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([name, count]) => ({ name, strikes: count, lockedOut: lockedSet.has(name) }));
}

function FeudStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap');

      .fd-stage {
        flex: 1;
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding: 32px 72px 28px;
        background:
          radial-gradient(80% 50% at 50% 0%, rgba(249, 115, 22, 0.22) 0%, transparent 70%),
          radial-gradient(60% 60% at 50% 100%, rgba(249, 115, 22, 0.08) 0%, transparent 70%),
          linear-gradient(180deg, #0a0815 0%, #0d0a1c 100%);
        font-family: 'Rajdhani', sans-serif;
        overflow: hidden;
      }
      .fd-stage-end {
        place-items: center;
        display: grid;
      }
      .fd-loading {
        margin: auto;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 24px;
        color: var(--gx-text-muted);
      }
      .fd-loading-eyebrow {
        font-family: 'Bebas Neue', 'Orbitron', sans-serif;
        font-size: 32px;
        letter-spacing: 6px;
        color: #fb923c;
        text-shadow: 0 0 24px rgba(249, 115, 22, 0.5);
      }
      .fd-loading-spinner {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        border: 4px solid rgba(249, 115, 22, 0.18);
        border-top-color: #f97316;
        animation: fd-spin 0.9s linear infinite;
      }
      @keyframes fd-spin {
        to { transform: rotate(360deg); }
      }

      .fd-spotlight {
        position: absolute;
        top: -160px;
        left: 50%;
        transform: translateX(-50%);
        width: 1200px;
        height: 700px;
        background: radial-gradient(50% 50% at 50% 50%, rgba(249, 115, 22, 0.22) 0%, transparent 70%);
        pointer-events: none;
        animation: fd-spotlight-sweep 8s ease-in-out infinite alternate;
      }
      .fd-spotlight-2 {
        top: auto;
        bottom: -200px;
        background: radial-gradient(50% 50% at 50% 50%, rgba(255, 215, 0, 0.08) 0%, transparent 70%);
        animation-duration: 11s;
      }
      @keyframes fd-spotlight-sweep {
        0% { transform: translateX(calc(-50% - 60px)); opacity: 0.85; }
        100% { transform: translateX(calc(-50% + 60px)); opacity: 1; }
      }

      .fd-banner {
        position: relative;
        z-index: 1;
        padding: 28px 44px;
        border-radius: 22px;
        background:
          linear-gradient(135deg, rgba(249, 115, 22, 0.22), rgba(249, 115, 22, 0.05)),
          radial-gradient(circle at 0% 0%, rgba(255, 255, 255, 0.08), transparent 50%);
        border: 2px solid rgba(249, 115, 22, 0.6);
        box-shadow:
          0 0 0 1px rgba(255, 255, 255, 0.05) inset,
          0 0 100px -20px rgba(249, 115, 22, 0.7);
        backdrop-filter: blur(14px);
        text-align: center;
        animation: gx-slide-in-down 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
      }
      .fd-banner-eyebrow {
        font-family: 'Bebas Neue', 'Orbitron', sans-serif;
        font-size: 18px;
        font-weight: 400;
        letter-spacing: 8px;
        text-transform: uppercase;
        color: #fb923c;
        margin-bottom: 8px;
        text-shadow: 0 0 12px rgba(249, 115, 22, 0.6);
      }
      .fd-banner-prompt {
        font-family: 'Bebas Neue', 'Orbitron', sans-serif;
        font-size: 44px;
        font-weight: 400;
        line-height: 1.05;
        letter-spacing: 1.5px;
        color: white;
        text-shadow:
          0 4px 14px rgba(0, 0, 0, 0.7),
          0 0 28px rgba(249, 115, 22, 0.25);
      }

      .fd-board {
        position: relative;
        z-index: 1;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 18px;
        flex: 1;
        min-height: 0;
      }
      .fd-col {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .fd-card {
        flex: 1;
        perspective: 1200px;
        min-height: 72px;
      }
      .fd-card-inner {
        position: relative;
        width: 100%;
        height: 100%;
        transform-style: preserve-3d;
        transition: transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
      }
      .fd-card.revealed .fd-card-inner {
        transform: rotateY(180deg);
      }
      .fd-card-face {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        gap: 20px;
        padding: 0 24px 0 0;
        border-radius: 14px;
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
      }
      .fd-card-front {
        background:
          linear-gradient(135deg, rgba(20, 20, 35, 0.95), rgba(40, 16, 60, 0.95)),
          radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.06), transparent 60%);
        border: 2px solid rgba(255, 255, 255, 0.12);
        box-shadow:
          0 0 0 1px rgba(255, 255, 255, 0.04) inset,
          0 8px 32px -8px rgba(0, 0, 0, 0.5);
      }
      .fd-card-back {
        background:
          linear-gradient(135deg, rgba(249, 115, 22, 0.5), rgba(249, 115, 22, 0.15));
        border: 2px solid #f97316;
        box-shadow:
          0 0 0 1px rgba(255, 255, 255, 0.12) inset,
          0 0 64px -10px rgba(249, 115, 22, 0.9);
        transform: rotateY(180deg);
      }
      .fd-card-rank {
        flex: 0 0 auto;
        width: 72px;
        height: 100%;
        min-height: 72px;
        display: grid;
        place-items: center;
        font-family: 'Bebas Neue', 'Orbitron', sans-serif;
        font-size: 44px;
        font-weight: 400;
        background: rgba(255, 255, 255, 0.04);
        color: rgba(255, 255, 255, 0.55);
        border-right: 2px solid rgba(255, 255, 255, 0.1);
        border-radius: 14px 0 0 14px;
        letter-spacing: 2px;
      }
      .fd-card-rank-revealed {
        background: linear-gradient(180deg, #fb923c, #f97316);
        color: #0b0918;
        border-right-color: rgba(255, 255, 255, 0.3);
        text-shadow: none;
        box-shadow: 0 0 28px rgba(249, 115, 22, 0.8);
      }
      .fd-card-q {
        flex: 1;
        font-family: 'Bebas Neue', 'Orbitron', sans-serif;
        font-size: 64px;
        font-weight: 400;
        color: rgba(255, 255, 255, 0.18);
        text-align: center;
        letter-spacing: 4px;
      }
      .fd-card-answer {
        flex: 1;
        font-family: 'Bebas Neue', 'Orbitron', sans-serif;
        font-size: 30px;
        font-weight: 400;
        letter-spacing: 2.5px;
        color: white;
        text-shadow:
          0 0 14px rgba(249, 115, 22, 0.6),
          0 2px 4px rgba(0, 0, 0, 0.5);
      }
      .fd-card-points {
        font-family: 'Bebas Neue', 'Orbitron', sans-serif;
        font-size: 44px;
        font-weight: 400;
        color: #fff5e6;
        text-shadow: 0 0 18px rgba(249, 115, 22, 0.9);
        min-width: 64px;
        text-align: right;
        letter-spacing: 1px;
      }

      .fd-footer {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }
      .fd-timer {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 8px 18px;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.12);
        color: white;
      }
      .fd-timer.warn {
        background: rgba(239, 68, 68, 0.18);
        border-color: rgba(239, 68, 68, 0.5);
        color: #fca5a5;
        animation: gx-timer-warn 0.6s ease-in-out infinite;
      }
      .fd-timer-num {
        font-family: 'JetBrains Mono', monospace;
        font-size: 22px;
        font-weight: 900;
      }

      .fd-strikes-ticker {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 6px 14px;
        border-radius: 999px;
        background: rgba(239, 68, 68, 0.1);
        border: 1px solid rgba(239, 68, 68, 0.32);
        max-width: 60%;
        overflow: hidden;
      }
      .fd-strikes-label {
        font-family: 'Bebas Neue', 'Orbitron', sans-serif;
        font-size: 12px;
        letter-spacing: 3px;
        color: rgba(252, 165, 165, 0.7);
      }
      .fd-strike-chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 3px 10px;
        border-radius: 999px;
        background: rgba(239, 68, 68, 0.16);
        border: 1px solid rgba(239, 68, 68, 0.42);
        font-size: 12px;
        color: #fecaca;
      }
      .fd-strike-chip.locked {
        background: rgba(239, 68, 68, 0.35);
        border-color: #ef4444;
        color: white;
        text-shadow: 0 0 6px rgba(239, 68, 68, 0.7);
      }
      .fd-strike-chip-name {
        font-weight: 700;
      }
      .fd-strike-chip-x {
        font-family: 'JetBrains Mono', monospace;
        letter-spacing: 1px;
      }

      .fd-survey-says {
        position: absolute;
        inset: 0;
        z-index: 5;
        display: grid;
        place-items: center;
        pointer-events: none;
        background: radial-gradient(circle at 50% 50%, rgba(249, 115, 22, 0.25), transparent 70%);
        animation: fd-flash-bg 2.2s ease-out forwards;
      }
      .fd-survey-says-text {
        font-family: 'Bebas Neue', 'Orbitron', sans-serif;
        font-size: 130px;
        letter-spacing: 8px;
        color: white;
        text-shadow:
          0 0 40px #f97316,
          0 0 80px rgba(249, 115, 22, 0.8),
          0 8px 24px rgba(0, 0, 0, 0.8);
        animation: fd-stamp-in 0.55s cubic-bezier(0.16, 1, 0.3, 1) both,
                   fd-stamp-out 0.45s ease-in 1.75s both;
      }
      @keyframes fd-flash-bg {
        0% { opacity: 0; }
        15% { opacity: 1; }
        100% { opacity: 0; }
      }
      @keyframes fd-stamp-in {
        0% { transform: scale(0.4); opacity: 0; letter-spacing: 24px; }
        70% { transform: scale(1.12); opacity: 1; letter-spacing: 6px; }
        100% { transform: scale(1); opacity: 1; letter-spacing: 8px; }
      }
      @keyframes fd-stamp-out {
        0% { transform: scale(1); opacity: 1; }
        100% { transform: scale(1.4); opacity: 0; }
      }

      .fd-strike-stamp {
        position: absolute;
        top: 50%;
        left: 50%;
        z-index: 6;
        transform: translate(-50%, -50%);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        pointer-events: none;
        animation: fd-strike-pop 0.4s cubic-bezier(0.16, 1, 0.3, 1) both,
                   fd-strike-fade 0.3s ease-in 1.3s both;
      }
      .fd-strike-x {
        font-size: 200px;
        line-height: 1;
        font-weight: 900;
        color: #ef4444;
        text-shadow:
          0 0 30px rgba(239, 68, 68, 0.95),
          0 0 80px rgba(239, 68, 68, 0.5),
          0 6px 18px rgba(0, 0, 0, 0.7);
        -webkit-text-stroke: 4px white;
      }
      .fd-strike-label {
        text-align: center;
        background: rgba(0, 0, 0, 0.78);
        padding: 8px 22px;
        border-radius: 10px;
        border: 2px solid #ef4444;
        box-shadow: 0 0 32px rgba(239, 68, 68, 0.6);
      }
      .fd-strike-name {
        font-family: 'Bebas Neue', 'Orbitron', sans-serif;
        font-size: 22px;
        letter-spacing: 2px;
        color: white;
      }
      .fd-strike-count {
        font-family: 'Bebas Neue', 'Orbitron', sans-serif;
        font-size: 14px;
        letter-spacing: 4px;
        color: #fca5a5;
      }
      @keyframes fd-strike-pop {
        0% { transform: translate(-50%, -50%) scale(0.3) rotate(-20deg); opacity: 0; }
        70% { transform: translate(-50%, -50%) scale(1.15) rotate(5deg); opacity: 1; }
        100% { transform: translate(-50%, -50%) scale(1) rotate(0deg); opacity: 1; }
      }
      @keyframes fd-strike-fade {
        0% { opacity: 1; }
        100% { opacity: 0; transform: translate(-50%, -50%) scale(1.2); }
      }

      .fd-end-card {
        padding: 56px 64px;
        border-radius: 28px;
        background: var(--gx-bg-strong);
        border: 2px solid rgba(249, 115, 22, 0.6);
        box-shadow: 0 0 120px -28px rgba(249, 115, 22, 0.7);
        text-align: center;
        min-width: 520px;
        animation: gx-slide-in-down 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
      }
      .fd-end-eyebrow {
        font-family: 'Bebas Neue', 'Orbitron', sans-serif;
        font-size: 16px;
        font-weight: 400;
        letter-spacing: 6px;
        text-transform: uppercase;
        color: #fb923c;
      }
      .fd-end-title {
        margin-top: 12px;
        font-family: 'Bebas Neue', 'Orbitron', sans-serif;
        font-size: 64px;
        font-weight: 400;
        letter-spacing: 2px;
        color: white;
        text-shadow: 0 0 32px rgba(249, 115, 22, 0.7);
      }
      .fd-end-board {
        margin-top: 28px;
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .fd-end-row {
        display: flex;
        align-items: center;
        gap: 18px;
        padding: 12px 20px;
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.03);
        font-size: 18px;
      }
      .fd-end-row.top {
        background: linear-gradient(90deg, rgba(249, 115, 22, 0.3), rgba(255, 255, 255, 0.03));
        border: 1px solid #f97316;
        box-shadow: 0 0 24px rgba(249, 115, 22, 0.4);
      }
      .fd-end-rank {
        font-family: 'JetBrains Mono', monospace;
        font-weight: 900;
        color: #f97316;
        min-width: 40px;
      }
      .fd-end-name { flex: 1; color: white; text-align: left; }
      .fd-end-score {
        font-family: 'JetBrains Mono', monospace;
        font-weight: 900;
        color: white;
      }
    `}</style>
  );
}
